import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_ORG_WEB_ACTIVITIES } from '../../../lib/graphql/queries/organizations';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

const PAGE_SIZE = 25;

type OrgWebActivitiesData = { organization?: { webActivities?: { items?: unknown[]; total?: number; hasMore?: boolean } } };

export const useOrgWebActivitiesGQL = ({
  dateRange,
  enabled = true,
  pageSize = PAGE_SIZE,
  eventTypes,
  deviceType,
  isGuest,
  search,
}: {
  dateRange: DateRange;
  enabled?: boolean;
  pageSize?: number;
  eventTypes?: string[];
  deviceType?: string;
  isGuest?: boolean;
  search?: string;
}) => {
  const { organization } = useOrganizationStore();
  const orgId = organization?.id ?? '';
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);

  const dateFrom = dateRange.from?.toISOString() ?? undefined;
  const dateTo = dateRange.to?.toISOString() ?? undefined;
  const offset = currentPage * currentPageSize;

  const { data, loading } = useQuery<OrgWebActivitiesData>(GET_ORG_WEB_ACTIVITIES, {
    variables: {
      organizationId: orgId,
      limit: currentPageSize,
      offset,
      dateFrom,
      dateTo,
      sortOrder: 'DESC',
      ...(eventTypes?.length ? { eventTypes } : {}),
      ...(deviceType ? { deviceType } : {}),
      ...(isGuest !== undefined ? { isGuest } : {}),
      ...(search?.trim() ? { search: search.trim() } : {}),
    },
    skip: !enabled || !orgId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  const webActivities = data?.organization?.webActivities?.items ?? [];
  const total = data?.organization?.webActivities?.total ?? 0;

  const totalPages = Math.ceil((data?.organization?.webActivities?.total ?? 0) / currentPageSize);

  const goToPage = useCallback((page: number) => setCurrentPage(page), []);
  const goToNextPage = useCallback(() => setCurrentPage((p) => Math.min(p + 1, totalPages - 1)), [totalPages]);
  const goToPrevPage = useCallback(() => setCurrentPage((p) => Math.max(p - 1, 0)), []);
  const changePageSize = useCallback((size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(0);
  }, []);

  return {
    webActivities,
    total,
    isLoading: loading,
    currentPage,
    pageSize: currentPageSize,
    goToPage,
    goToNextPage,
    goToPrevPage,
    changePageSize,
  };
};
